<template>
  <div class="period-wr">
    <!-- date select -->
    <div
      :class="[
        'input-block-period',
        'input-padding-left',
        'input-padding-bottom',
        'select-respons',
        {'hide-custom-select': period === 1}
      ]"
    >
      <UiSelect
        v-model="period"
        class="ui-select__period"
        popper-class="ui-select__period-dropdown"
        prepend-icon="calendar-alt"
        :list="periodItems"
        :width="!isMobile ? 220 : ''"
        @change="change($event, true)"
        @update:model-value="handleValueChanged"
      />
      <span
        v-if="customPeriodText"
        class="custom-data"
      >{{ customPeriodText }}</span>
      <i
        v-if="customPeriodText"
        class="fas cust fa-times-circle"
        @click="removeCustom($event)"
      />
    </div>

    <!-- date picker calendar -->
    <div
      :class="[
        'select-respons',
        'input-block-period',
        'form-group',
        'mt-0',
        'mb-0'
      ]"
    >
      <ElConfigProvider :locale="elementLang">
        <ElDatePicker
          ref="datepicker"
          v-model="datepicker"
          class="date-picker"
          type="daterange"
          :picker-options="{
            firstDayOfWeek: firstDay,
          }"
          placement="bottom-start"
          format="DD MMM YYYY"
          @change="setRange($event, true)"
          @blur="blur"
        />
      </ElConfigProvider>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import UrlMixin from '@/components/Query/url-mixin';
import ResponsiveMixin from '@/components/Helpers/responsive-mixin';
import DateMixin from '@/views/Dashboard/Reports/dateMixin';
import { useElementLang } from '@/helpers/elementLang';

export default {
  mixins: [
    UrlMixin, 
    ResponsiveMixin,
    DateMixin,
  ],
  props: {
    settings: {
      default: null,
    },
    defaultAllTime: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update', 'change'],
  setup: () =>  {
    const { elementLang } = useElementLang();
    return { elementLang };
  },
  data() {
    return {
      range: [],
      period: this.defaultAllTime ? 2 : 30,
      customPeriodText: '',
      datepicker: '',
      prev: this.defaultAllTime ? 2 : 30,
      periodItems: [
        {
          id: 1,
          text: this.$t('periodWrapper.custom'),
          field: 'custom',
        }, {
          id: 2,
          text: this.$t('periodWrapper.all_period'),
          field: 'all_period',
        }, {
          id: 7,
          text: this.$t('periodWrapper.this_week'),
          field: 'this_week',
        }, {
          id: -70,
          text: this.$t('periodWrapper.previous_week'),
          field: 'previous_week',
        }, {
          id: -7,
          text: this.$t('periodWrapper.last_7'),
          field: 'last_7',
        }, {
          id: -14,
          text: this.$t('periodWrapper.last_14'),
          field: 'last_14',
        }, {
          id: 30,
          text: this.$t('periodWrapper.this_month'),
          field: 'this_month',
        }, {
          id: -30,
          text: this.$t('periodWrapper.previous_month'),
          field: 'previous_month',
        }, {
          id: 100,
          text: this.$t('periodWrapper.last_30'),
          field: 'last_30',
        }, {
          id: 365,
          text: this.$t('periodWrapper.this_year'),
          field: 'this_year',
        }, {
          id: -365,
          text: this.$t('periodWrapper.previous_year'),
          field: 'previous_year',
        }, {
          id: -3650,
          text: this.$t('periodWrapper.last_365'),
          field: 'last_365',
        },
      ].filter((el) => (this.defaultAllTime ? true : el.id !== 2)),
      locale: '',
      loh: false,
    };
  },
  computed: {
    // customPlaceholder() {
    //   return !this.periodItems.some(e => e.id === +this.period);
    // },
    isMobileParams() {
      return {
        activated: this.isComponentActivated,
        isMobile: this.$store.state.misc?.breakthrough?.sm,
      };
    },
    firstDay() {
      const firstDay = this.$store.state.profile.user.first_day_of_week;
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      return days.findIndex((d) => d === firstDay) + 1 || 1;
    },
  },
  watch: {
    period(value, oldValue) {
      if (this.customPeriodText) {
        this.$nextTick(() => {
          this.period = 1;
        });
      } else if (+value === 1) {
        this.$nextTick(() => {
          this.period = +oldValue;
        });
      } else if (value !== oldValue && +oldValue !== 1) {
        this.prev = +oldValue;
      }
    },
    '$locale.value': {
      immediate: true,
      handler(value) {
        if (this.range && this.range.length) {
          const [a, b] = this.$_.clone(this.range);

          moment.updateLocale(value, {
            week: {
              dow: this.firstDay < 7 ? this.firstDay : 0,
            },
          });

          this.customPeriodText = `${moment(a)
            .locale(value)
            .format('DD MMM YYYY')} ${this.$t('periodWrapper.to')} ${moment(b)
            .locale(value)
            .format('DD MMM YYYY')}`;
        }
        this.periodItems.map((e) => {
          e.text = this.$t(`periodWrapper.${e.field}`);
        });
      },
    },
    settings: {
      immediate: true,
      deep: true,
      handler(value, oldValue) {
        if (!this.$_.isEqual(value, oldValue)) {
          this.initPeriod(value);
        }
      },
    },
    firstDay() {
      this.initLocale();

      if (typeof this.period === 'number') {
        this.setPeriod(+this.period);
      }
    },
  },
  activated() {
    this.initLocale();
  },
  mounted() {
    this.initLocale();
  },
  updated() {
    this.locale = this.$locale.value;
  },
  methods: {
    handleValueChanged(value) {
      if (value === 1 && this.range.length) {
        setTimeout(() => {
          this.$refs.datepicker.handleOpen();
        }, 100);
      }
    },
    onBlur() {
      // vue3-migration -- Select2
      // this.$refs.select.select2.select2('close')
    },
    initPeriod(settings) {
      if (settings && settings.period) {
        this.period = settings.period;
        const isCustomPeriod = typeof settings.period === 'string' || settings.period == 1;

        if (isCustomPeriod && settings.to && settings.from) {
          this.setRange([settings.from, settings.to]);
        } else {
          this.setPeriod(settings.period);
        }
      } else if (settings && settings.range) {
        this.range = settings.range;
        this.setRange(settings.range);
      } else {
        this.period = this.defaultAllTime ? 2 : 30;
        this.setPeriod(this.period);
      }
    },
    initLocale() {
      moment.updateLocale(localStorage.getItem('language'), {
        week: {
          dow: this.firstDay < 7 ? this.firstDay : 0,
        },
      });
    },
    blur(v) {
      if (this.period == 1 && !this.customPeriodText) {
        this.period = this.prev;
        this.setPeriod(this.prev);
      }
    },
    setRange(range, changeEvent = false) {
      if (range.length !== 2) {
        this.period = this.prev;
        this.change(this.prev, changeEvent);
        return;
      }
      range = range.map((e, i) => {
        if (!i) {
          return `${moment(e)
            .format('YYYY-MM-DD 00:00:00')}`;
        }
        return `${moment(e)
          .format('YYYY-MM-DD 23:59:59')}`;
      });

      this.period = 1;
      this.range = range;
      let [a, b] = this.$_.clone(this.range);
      this.customPeriodText = `${moment(a)
        .format('DD MMM YYYY')} ${this.$t('periodWrapper.to')} ${moment(b)
        .format('DD MMM YYYY')}`;

      if (a.length === 10) {
        a = `${a} 00:00:00`;
      }

      if (b.length === 10) {
        b = `${b} 23:59:59`;
      }

      this.$emit('update', {
        from: a,
        to: b,
        period: this.period,
      });

      setTimeout(() => {
        if (changeEvent) {
          this.$emit('change', {
            from: a,
            to: b,
            period: this.period,
          });
        }
      });
    },
    change(period, changeEvent = false) {
      this.setPeriod(+period || (this.defaultAllTime ? 2 : 30), changeEvent);
    },
    setPeriod(period, changeEvent = false) {
      if (!period) return;

      if (period === 1 && this.range.length) {
        this.datepicker = [
          moment(this.range[0]).format('YYYY-MM-DD 00:00:00'),
          moment(this.range[1]).format('YYYY-MM-DD 00:00:00'),
        ];

        return;
      }

      // invoke custom dates
      if (typeof period === 'object') {
        //
        // this.$emit('update', period);
        return;
      } else if (typeof period === 'string') {
        let [a, b] = period.split(this.$t('periodWrapper.to'))
          .map((e) => e.trim());

        if (a.length === 10) {
          a = `${a} 00:00:00`;
        } else {
          a = `${moment(a)
            .format('YYYY-MM-DD 00:00:00')}` !== 'Invalid date' ? `${moment(a)
              .format('YYYY-MM-DD 00:00:00')}` : this.setPeriod(30);
          return;
        }

        if (b.length === 10) {
          b = `${b} 23:59:59`;
        } else {
          b = `${moment(b)
            .format('YYYY-MM-DD 23:59:59')}`;
        }

        this.range = [a, b];
        this.customPeriodText = `${moment(a)
          .format('DD MMM YYYY')} ${this.$t('periodWrapper.to')} ${moment(b)
          .format('DD MMM YYYY')}`;
        this.$emit('update', {
          from: a,
          to: b,
          period: this.period,
        });

        // if(changeEvent) {
        //   this.$emit('change', {
        //     from: a,
        //     to: b,
        //     period: this.period,
        //   });
        // }
        return;
      }

      moment.updateLocale(this.$locale.value, {
        week: {
          dow: this.firstDay === 7 ? 0 : 1,
        },
      });

      if (period !== 1) {
        this.customPeriodText = '';
        this.range = [];
        this.datepicker = '';
      } else {
        if (changeEvent) {
          setTimeout(() => {
            this.$refs.datepicker.handleOpen();
          }, 100);
        }
        return;
      }

      let {start, end} = this.getDaterangeByPeriod(period);

      if (period !== 1) {
        this.$emit('update', {
          from: start,
          to: end,
          period,
        });

        if (changeEvent) {
          this.$emit('change', {
            from: start,
            to: end,
            period,
          });
        }
      }
    },
    removeCustom(e) {
      e.stopPropagation();
      this.period = this.defaultAllTime ? 2 : 30;
      this.customPeriodText = '';
      this.change(this.period, true);
    },
  },
};
</script>

<style lang="scss">
// @import '~flatpickr/dist/flatpickr';

// дубликатов нет
@import 'src/assets/theme/default/components/periodWrapper';
</style>
